import React, { useState } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { Form, Button, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import { Link, useLocation } from 'react-router-dom';
import { resetPassword } from '../services/api';
import { Helmet } from 'react-helmet-async';

const ResetPassword: React.FC = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token') || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Invalid reset link. Please request a new one.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, newPassword);
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonPage><IonContent>
    <main className="container py-4" style={{ maxWidth: 440, margin: '0 auto' }}>
      <Helmet><title>Reset Password | QA Master</title></Helmet>
      <div>
              <div style={{ textAlign: 'center', marginTop: 60, marginBottom: 40 }}>
                <h1 style={{
                  fontSize: 32, fontWeight: 700, margin: 0,
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
                }}>QA Master</h1>
                <p style={{ color: '#666', marginTop: 8 }}>Set a new password</p>
              </div>

        <div className="bg-white rounded-3 shadow-sm p-4">
          {success ? (
            <div className="text-center py-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle mx-auto mb-3"
                style={{ width: 64, height: 64, background: '#dcfce7', fontSize: 28 }}
              >
                &#10003;
              </div>
              <h5 className="mb-2">Password Reset!</h5>
              <p className="text-muted small">
                Your password has been updated. You can now sign in.
              </p>
              <Link to="/login">
                <Button className="btn-brand-gradient mt-2">Sign In</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">New Password</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                  />
                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={() => setShowNewPassword(s => !s)}
                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showNewPassword ? <EyeSlash /> : <Eye />}
                  </Button>
                </InputGroup>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label className="fw-semibold small">Confirm Password</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    required
                  />
                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={() => setShowConfirmPassword(s => !s)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeSlash /> : <Eye />}
                  </Button>
                </InputGroup>
              </Form.Group>

              <Button
                type="submit"
                className="w-100 btn-brand-gradient fw-semibold"
                disabled={loading}
              >
                {loading ? <Spinner animation="border" size="sm" /> : 'Reset Password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </main>
    </IonContent></IonPage>
  );
};

export default ResetPassword;
